import { Alert, AlertColor, Box, Button, Divider, FormControlLabel, IconButton, Snackbar, Switch, TextField, Typography } from "@mui/material";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { Check } from "@mui/icons-material";
import { GroupsTable } from "../GroupsTable/GroupsTable";
import { useEffect, useState } from "react";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from "react-i18next";
import { AddUserGroup, CreateUser, EditUser, GetUser, GetUserByEmail, RemoveUserGroup, VerifyUserAddingGuest } from "../../../../../services/UserServices";
import { LoginType } from "../../../../../utils/constants/LoginTypeConstants";
import LoaderComp from "../../../../../components/Loader/LoaderComp";
import { Group } from "../../../../../model/GroupModel";
import { UserCreate, UserGroupsVO, UserOrganization } from "../../../../../model/UserModel";
import { Organization } from "../../../../../model/OrganizationModel";
import { GetOneOrganization } from "../../../../../services/OrganizationServices";
import KeydriveSnackbar from "../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";
import { KeyDriveDialog } from "../../../../../components/KeydriveComponents/Dialog/KeyDriveDialog";

export const UserPanel = () => {
    const [isEditPage, setIsEditPage] = useState<boolean>(false);
    const [twoFa, setTwofa] = useState<boolean>(true);
    const [username, setUsername] = useState<string | undefined>(undefined);
    const [email, setEmail] = useState<string | undefined>(undefined);
    const [phone, setPhone] = useState<string | undefined>(undefined);
    const [loginType, setLoginType] = useState<number>(0)
    const [userEditId, setUserEditId] = useState<string | undefined>(undefined);
    const [isUserNameError, setIsUsernameError] = useState<boolean>(false);
    const [isEmailError, setIsEmailError] = useState<boolean>(false);
    const [isPhoneError, setIsPhoneError] = useState<boolean>(false);
    const [loading, setIsLoading] = useState<boolean>(true);
    const [groupList, setGroupList] = useState<Group[]>([]);
    const [userOriginalGroupsIds, setUserOriginalGroupsIds] = useState<string[]>([]);
    const [organization, setOrganization] = useState<Organization | undefined>();
    const [organizationsGroups, setOrganizationsGroups] = useState<Group[]>([]);
    const [messageSnack, setMessageSnack] = useState<string | undefined>(undefined);
    const [colorSnack, setColorSnack] = useState<AlertColor | undefined>();
    const [openKeyDriveDialog, setKeyDriveDialog] = useState<boolean>(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { nameOrg } = useParams();
    const location = useLocation();
    const {t} = useTranslation();

    useEffect(() => {
        const userId = searchParams.get('userId');

        if(userId){
            setUserEditId(userId);
            setIsEditPage(true);
            GetUser(userId)
            .then((data) => {
                setUsername(data.name)
                setEmail(data.login)
                setLoginType(data.loginType)

                if(data.phone){
                    setPhone(data.phone)
                }

                if (data.twoFaAuthEnabled == true){
                    setTwofa(true);
                }else (
                    setTwofa(false)
                );

                if(data.groups){
                    setUserOriginalGroupsIds(data.groups)
                }
            })
            .catch((err) => handleSnackbarWithMessage(err.response.data, 'error'))
            .finally(() => setIsLoading(false));
        } else {
            setIsLoading(false);
        }

        if(nameOrg){
            GetOneOrganization(nameOrg)
            .then((data) => {
                if(data){
                    setOrganization(data)
                }
            })
        }

    }, [groupList, searchParams, userEditId, nameOrg]);

    const handleBackClick = () => {
        navigate(`/${nameOrg}/adminConsole/usersAndGroups`);
    };

    const handleChangeUsername = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value != undefined) {
            setUsername(event.target.value);
        }
    }

    const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const email = event.target.value;

        setEmail(email);

        if (emailRegex.test(email)) {
            setIsEmailError(false);
        } else {
            setIsEmailError(true);
        }
    };

    const handleChangePhone = (event: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = event.target.value.replace(/\D/g, ""); 

        if (inputValue.length <= 11) {
            inputValue = inputValue.replace(/^(\d{2})(\d)/g, "($1) $2");
            inputValue = inputValue.replace(/(\d{5})(\d)/, "$1-$2");
        }

        if (inputValue.replace(/\D/g, "").length < 11) {
            setIsPhoneError(true);
        }else{
            setIsPhoneError(false);
        }

        setPhone(inputValue);
    };

    const handleToggleTwoFa = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setTwofa(event.target.checked);
    };

    const validateFields = () => {
        let isValid = true;

        if (!username || username.length < 3) {
            setIsUsernameError(true);
            isValid = false;
        } else {
            setIsUsernameError(false);
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            setIsEmailError(true);
            isValid = false;
        } else {
            setIsEmailError(false);
        }

        if (phone && phone.replace(/\D/g, "").length < 11) {
            setIsPhoneError(true);
            isValid = false;
        } else {
            setIsPhoneError(false);
        }

        return isValid;
    };

    const addGroup = (userGroup: UserGroupsVO) => {
        AddUserGroup(userGroup)
        .then()
        .catch((err) => handleSnackbarWithMessage(err.response.data, 'error'))
    }

    const removeGroup = (userGroup: UserGroupsVO) => {
        RemoveUserGroup(userGroup)
        .catch((err) => handleSnackbarWithMessage(err.response.data, 'error'))
    }
    
    const updateUser = () => {
        const userOrganization: UserOrganization = {
            idOrganization: organization!.id!,
            isAdminOrganization: false
        }

        const userEdit: UserCreate = {
            id: userEditId,
            name: username,
            login: email,
            phone: phone,
            organization: userOrganization,
            twoFaAuthEnabled: twoFa,
            loginType: loginType
        }
        
        const groupListIds = groupList.map((group) => group.id)
        const groupsNotSelected = organizationsGroups.filter((group) => !groupListIds.includes(group.id));

        groupsNotSelected.forEach((group) => {
            const userGroup: UserGroupsVO = {
                groupId: group.id,
                userId: userEditId,
            };
            removeGroup(userGroup);
        });

        const groupsToAdd = groupListIds.filter(group => !userOriginalGroupsIds.includes(group!));

        groupsToAdd.forEach(group => {
            const userGroup: UserGroupsVO = {
                groupId: group,
                userId: userEditId,
            };
            addGroup(userGroup);
        });

        EditUser(userEdit)
            .then((data) => {
                handleSnackbarWithMessage((data && data.length > 0)?data:t("AlteradoComSucesso.message"), 'success');
            }
          )
            .catch((err) => handleSnackbarWithMessage(err.response.data, 'error'))
            .finally(() => {
                setKeyDriveDialog(false);
            })
    }
    const createUser = () => {
        setIsLoading(true);

        const userOrganization: UserOrganization = {
            idOrganization: organization!.id!,
            isAdminOrganization: false,
        }

        const userCreate: UserCreate = {
            name: username,
            login: email,
            phone: phone,
            organization: userOrganization,
            twoFaAuthEnabled: twoFa,
            loginType: loginType
        }

        CreateUser(userCreate)
            .then((data) => { 
                handleSnackbarWithMessage(data, 'success'); 
                GetUserByEmail(userCreate.login!).then(dataUser => {
                    setUserEditId(dataUser.id);

                const params = new URLSearchParams(location.search);
                params.set('userId', dataUser.id!);
                navigate(`${location.pathname}?${params.toString()}`, { replace: true });

                if(groupList && groupList.length > 0) {
                    groupList.forEach((group) => {
                        const userGroup: UserGroupsVO = {
                            groupId: group.id,
                            userId: dataUser.id,
                        }
                        
                        addGroup(userGroup)
                    })
                } 

                searchParams.set('userId', dataUser.id!);
                })
            })
            .catch((err) => handleSnackbarWithMessage(err.response.data, 'error' ))
            .finally(() => {
                setIsLoading(false);
                setKeyDriveDialog(false);
            });
        }
    
    const handleSave = () => {
        if (validateFields()) {
            if (isEditPage && userEditId) {
                updateUser();
            } else {
                createUser();
            }
        }
    }

    const handleSnackbarWithMessage = (message: string, color: AlertColor) => {
        setMessageSnack(message)
        setColorSnack(color)
    }


    const styles = {
        container: {
            background: '#F7F8FA',
            padding: '30px',
        },
        panel: {
            background: '#FFFFFF',
            border: '1px solid #0000001A',
            borderRadius: '4px',
            height: '100%',
            padding: '30px',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
        },
        iconButton: {
            borderRadius: '4px',
            font: 'normal normal normal 14px / 21px Segoe UI',
            color: "#22638D",
            '&:hover': {
                backgroundColor: "#F4F7F9",
            },
        },
        headerText: {
            display: 'flex',
            alignItems: 'center',
            font: 'normal normal 600 20px/ 27px Segoe UI',
        },
        saveButton: {
            height: '36px',
            backgroundColor: '#22638D',
            textTransform: 'none',
            font: 'normal normal normal 14px/21px Segoe UI',
            whiteSpace: 'nowrap',
        },
        cancelButton: {
            height: '36px',
            textTransform: 'none',
            font: 'normal normal normal 14px/21px Segoe UI',
            whiteSpace: 'nowrap',
        },
        sectionTitle: {
            font: 'normal normal 600 16px/ 24px Segoe UI',
        },
        label: {
            font: 'normal normal normal 12px / 18px Segoe UI',

        },
        textField: {
            width: '100%',
            borderBottom: '1px solid black',

            '&.MuiTextField-root': {
                border: 'none', 
            },
        },
        description: {
            font: 'normal normal normal 14px / 21px Segoe UI',
            opacity: 0.7,
        },
        switch: {
            '& .MuiSwitch-switchBase': {
                color: '#969696',
                '&.Mui-checked': {
                    color: '#22638D',
                },
            },
            '& .MuiSwitch-track': {
                backgroundColor: '#BDBDBD',
            },
        },
        twoFaText: {
            font: 'normal normal normal 14px / 21px Segoe UI',
            opacity: 0.5,
        },
    };

    if (loading) {
        return (
            <Box sx={styles.container}>
                <Box sx={styles.panel}>
                    <Box sx={{ width: "100%", display:"flex", justifyContent:"center" }}>
                        <LoaderComp/>
                    </Box>
                </Box>
            </Box>
        )
    }

    return (
        <Box sx={styles.container}>
            <Box sx={styles.panel}>
                <Box>
                    <IconButton sx={styles.iconButton} onClick={handleBackClick}>
                        <ArrowBackIosIcon sx={{ height: '12px' }} />
                        <Typography>{t("AdminConsole.Back")}</Typography>
                    </IconButton>
                </Box>

                <Box display={'flex'} justifyContent={'space-between'}>
                    
                        {isEditPage ? 
                        <>
                        <Typography sx={styles.headerText}>
                                <EditIcon sx={{ width: '24px', height: '24px', marginRight: '12px', color: '#22638D' }} />
                                {t("AdminConsole.EditInfo")}
                        </Typography>

                        <Box display="flex" gap='10px'>
                            <Button variant="outlined" sx={styles.cancelButton} onClick={handleBackClick}>
                                    {t("AdminConsole.Cancel")}
                            </Button>
                            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} sx={styles.saveButton}>
                                    {t("AdminConsole.Save")}
                            </Button>
                        </Box>
                        </>
                        :
                        <>
                        <Typography sx={styles.headerText}>
                            <PersonAddAlt1Icon sx={{ width: '24px', height: '24px', marginRight: '12px', color: '#22638D' }} />
                            {t("AdminConsole.NewUser")}
                        </Typography>

                        <Button variant="contained" startIcon={<Check />} onClick={() => {
                            handleSave();
                        }} sx={styles.saveButton}>
                            {t("AdminConsole.Save")}
                        </Button>
                        </>
                        }
                </Box>

                <Divider />

                <Typography sx={styles.sectionTitle}>{t("AdminConsole.UsersInfo")}</Typography>

                <Box sx={{ display: 'flex', gap: '20px' }}>
                    <>
                        <Box flex={1}>
                            <Typography sx={styles.label} color={isUserNameError ? 'error' : ''}>{t("AdminConsole.Name")}*</Typography>
                            <TextField
                                error={isUserNameError ? true : false}
                                value={username}
                                onChange={handleChangeUsername}
                                sx={styles.textField}
                                required
                                variant="standard"
                                placeholder={t("AdminConsole.TypeUserName")}
                                helperText={isUserNameError ? t("AdminConsole.InvalidUsername") : undefined}
                            />
                        </Box>

                        <Box flex={1}>
                            <Typography sx={styles.label} color={isEmailError ? 'error' : ''}>E-mail*</Typography>
                            <TextField
                                error={isEmailError ? true : false}
                                value={email}
                                onChange={handleChangeEmail}
                                sx={styles.textField}
                                required
                                variant="standard"
                                placeholder={t("AdminConsole.TypeUserEmail")}
                                helperText={isEmailError ? t("AdminConsole.InvalidEmail") : undefined}
                                disabled={userEditId !== undefined}                                    
                                inputProps={{maxLength: 254}}
                            />
                        </Box>

                        <Box flex={1}>
                            <Typography sx={styles.label} color={isPhoneError ? 'error' : ''}>{t("AdminConsole.Phone")}</Typography>
                            <TextField
                                error={isPhoneError ? true : false}
                                value={phone}
                                onChange={handleChangePhone}
                                inputProps={{ maxLength: 15 }}
                                sx={styles.textField}
                                required
                                variant="standard"
                                placeholder={t("AdminConsole.TypeUserPhone")}
                                helperText={isPhoneError ? t("AdminConsole.InvalidPhone") : undefined}
                            />
                        </Box>
                    </>
                    
                </Box>

                <Typography sx={styles.sectionTitle}>{t("AdminConsole.AvailableGroups")}</Typography>
                <Box>
                    <GroupsTable isGroupTable={false} nameOrg={nameOrg!} userEditId={userEditId} setUserGroups={setGroupList} setOrganizationGroups={setOrganizationsGroups}/>
                </Box>

                <Typography sx={styles.sectionTitle}>{t("AdminConsole.LoginWithTwoFa")}</Typography>
                <Typography sx={styles.description}>
                    {t("AdminConsole.EnableTwoFa")}
                </Typography>

                <Box display={'flex'} alignItems={'center'}>
                    {loading ? (
                        <LoaderComp />
                    ) : (

                        <Switch checked={twoFa} onChange={handleToggleTwoFa} sx={styles.switch} />
                    )}
                    <Typography sx={styles.twoFaText}>
                        {t("AdminConsole.AllowTwoFa")}
                    </Typography>
                </Box>
            </Box>
        
            <KeydriveSnackbar 
                open={messageSnack != undefined}
                message={messageSnack}
                variant={colorSnack}
                onClose={() => setMessageSnack(undefined)}
            />
        </Box>
    );
};
